import React, { createContext, ReactNode, useContext, useEffect } from "react"

import { SocketService } from "utils/services"

interface SocketContextValue {
    send: <T>(data: T) => void
    onMessage: <T>(callback: (data: T) => void) => void
    offMessage: () => void
    onReconnect: (callback: () => void) => void
    offReconnect: (callback: () => void) => void
}

interface SocketProviderProps {
    url: string
    children: ReactNode
    onConnectError?: (error: Event) => void
}

export const SocketContext = createContext<SocketContextValue | null>(null)

export const SocketProvider = ({ url, children, onConnectError }: SocketProviderProps) => {
    const socketService = SocketService.getInstance()

    useEffect(() => {
        socketService.connect(url, onConnectError)

        // Clean up the connection when the component unmounts
        return () => {
            socketService.disconnect()
        }
    }, [url])

    const value: SocketContextValue = {
        send: socketService.send.bind(socketService),
        onMessage: (callback) => {
            socketService.onMessage(callback)
        },
        offMessage: () => {
            socketService.offMessage()
        },
        onReconnect: (callback) => {
            socketService.onReconnect(callback)
        },
        offReconnect: (callback) => {
            socketService.offReconnect(callback)
        },
    }

    return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export const useSocket = (): SocketContextValue | null => useContext(SocketContext)
